import React, { useMemo } from 'react';
import { BottomNavigation, BottomNavigationAction, Box, Paper, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCaja } from '../context/CajaContext';

const MobileBottomNav = ({ onOpenMenu }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { cajaAbierta, cajaRequired } = useCaja();

  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isCajaLocked = cajaRequired && !cajaAbierta;

  const items = useMemo(() => {
    const role = user?.role;
    const canSeeVentas = role && role !== 'bodega';
    const canSeeCaja = role && (role === 'admin' || role === 'cajero' || isCajaLocked);

    const list = [
      { value: '/dashboard', label: 'Inicio', icon: <DashboardIcon /> },
      ...(canSeeVentas ? [{ value: '/ventas', label: 'Ventas', icon: <TrendingUpIcon /> }] : []),
      ...(canSeeCaja ? [{ value: '/caja', label: 'Caja', icon: <PointOfSaleIcon /> }] : []),
      { value: '/inbox', label: 'Chat', icon: <ChatBubbleOutlineIcon /> },
      { value: 'more', label: 'Menu', icon: <MenuIcon /> },
    ];

    return list.slice(0, 5);
  }, [user?.role, isCajaLocked]);

  const currentValue = useMemo(() => {
    const match = items.find((item) => item.value !== 'more' && pathname.startsWith(item.value));
    return match?.value ?? false;
  }, [items, pathname]);

  if (!isPhone) return null;

  const containerBg =
    theme.palette.mode === 'light'
      ? alpha(theme.palette.background.paper, 0.92)
      : alpha(theme.palette.background.paper, 0.82);

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        pb: 'max(env(safe-area-inset-bottom), 8px)',
        zIndex: (t) => t.zIndex.appBar + 1,
        pointerEvents: 'none',
      }}
    >
      <Paper
        elevation={10}
        sx={{
          pointerEvents: 'auto',
          width: 'calc(100% - 24px)',
          maxWidth: 420,
          mx: 'auto',
          borderRadius: 999,
          overflow: 'hidden',
          backdropFilter: 'blur(14px)',
          bgcolor: containerBg,
          border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'light' ? 0.6 : 0.35)}`,
        }}
      >
        <BottomNavigation
          showLabels={false}
          value={currentValue}
          onChange={(_, newValue) => {
            if (newValue === 'more') {
              onOpenMenu?.();
              return;
            }
            if (typeof newValue === 'string') navigate(newValue);
          }}
          sx={{
            height: 62,
            bgcolor: 'transparent',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 0,
              px: 1.5,
              color: 'text.secondary',
            },
            '& .Mui-selected': {
              color: 'primary.main',
            },
            '& .MuiBottomNavigationAction-label': { display: 'none' },
          }}
        >
          {items.map((item) => (
            <BottomNavigationAction key={item.value} value={item.value} label={item.label} icon={item.icon} />
          ))}
        </BottomNavigation>
      </Paper>
    </Box>
  );
};

export default MobileBottomNav;
